import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';

import { SupportedLanguage } from '../../models/supported-language';
import { SUPPORTED_LANGUAGES } from '../../providers/supported-languages.token';
import { LanguageInputComponentModule } from '../language-input/language-input.component';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { LoadingOverlayComponent } from '@shared/ui';

export interface LanguageRecordDialogData {
  values?: Record<string, string>;
  requiredLanguages?: string[];
  titleKey?: string;
  closeOnSubmit?: boolean;
}

@Component({
  selector: 'ts-language-record-dialog',
  templateUrl: './language-record-dialog.component.html',
  styleUrls: ['./language-record-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LanguageInputComponentModule,
    ReactiveFormsModule,
    TranslateModule,
    MatDialogModule,
    LoadingOverlayComponent,
  ],
})
export class LanguageRecordDialogComponent implements OnInit, OnDestroy {
  form = new UntypedFormGroup({ values: new UntypedFormControl({}) });
  titleKey = '';
  missingLanguages: string[] = [];

  /** True while the opener has declared a save in flight; renders the loading overlay. */
  pending = false;
  /** Translation key of the failure the opener wants shown inline, if any. */
  errorKey?: string;
  /** Interpolation params for {@link errorKey}. */
  errorParams?: Record<string, unknown>;

  private readonly _submitted = new Subject<Record<string, string>>();
  /** Emits the values map on every valid submission while `closeOnSubmit` is false. */
  readonly submitted: Observable<Record<string, string>> = this._submitted.asObservable();

  private _disableCloseBeforePending = false;

  constructor(
    @Optional() public dialogRef: MatDialogRef<LanguageRecordDialogComponent, Record<string, string>>,
    @Inject(MAT_DIALOG_DATA) @Optional() public data: LanguageRecordDialogData | null,
    @Inject(SUPPORTED_LANGUAGES) public supportedLanguages: SupportedLanguage[]
  ) {}

  get closeOnSubmit(): boolean {
    return this.data?.closeOnSubmit ?? true;
  }

  ngOnInit(): void {
    const required = this.data?.requiredLanguages ?? ['en'];
    this.titleKey = this.data?.titleKey ?? 'title.specify_language';

    const valuesControl = new UntypedFormControl(this.data?.values ?? {}, this.requiredLanguagesValidator(required));
    this.form = new UntypedFormGroup({ values: valuesControl });

    valuesControl.statusChanges.subscribe(() => {
      const error = valuesControl.errors?.['requiredLanguagesMissing'] as { missing: string[] } | undefined;
      this.missingLanguages = error?.missing ?? [];
    });
    this.missingLanguages =
      (valuesControl.errors?.['requiredLanguagesMissing'] as { missing: string[] } | undefined)?.missing ?? [];
  }

  ngOnDestroy(): void {
    this._submitted.complete();
  }

  submit(): void {
    if (this.pending || this.form.invalid) return;
    const values = this.form.value.values as Record<string, string>;
    if (this.closeOnSubmit) {
      this.dialogRef?.close(values);
    } else {
      this.setError(null);
      this._submitted.next(values);
    }
  }

  close(): void {
    if (this.pending) return;
    this.dialogRef?.close();
  }

  /**
   * Covers the dialog with the loading overlay and blocks the backdrop and Escape routes out of it
   * while `pending` is true, restoring the dialog's own `disableClose` when it is cleared.
   */
  setPending(pending: boolean): void {
    if (this.pending === pending) return;
    if (pending) {
      this._disableCloseBeforePending = this.dialogRef?.disableClose ?? false;
      if (this.dialogRef) this.dialogRef.disableClose = true;
    } else if (this.dialogRef) {
      this.dialogRef.disableClose = this._disableCloseBeforePending;
    }
    this.pending = pending;
  }

  /**
   * Shows `key` translated with `params` inline, or clears the message when `key` is null.
   * The dialog never composes a message of its own and never inspects an exception.
   */
  setError(key: string | null, params?: Record<string, unknown>): void {
    this.errorKey = key ?? undefined;
    this.errorParams = key ? params : undefined;
  }

  private requiredLanguagesValidator(required: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value ?? {}) as Record<string, string>;
      const missing = required.filter((lang) => {
        const v = value[lang];
        return typeof v !== 'string' || v.trim() === '';
      });
      return missing.length ? { requiredLanguagesMissing: { missing } } : null;
    };
  }
}
